import { Download } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils/cn";

import { useReportFacets } from "../api/hooks";
import type { EnterpriseFilterState, FilterOptions } from "../api/types";
import {
  AdvancedFilters,
  FilterChip,
  FilterField,
  MonthInput,
  MultiSelectFilter,
  SearchInput,
  filterControlClass,
} from "./ui";

/**
 * The reports filter bar: a keyword search plus dropdown facets, with active
 * filters shown as removable chips.
 *
 * The controls are the Django page's, which is the reference for which
 * filters each report offers. `showAnswerFilters` is the enterprise list
 * (MRC, the issue → goal → action answer facets, answer type); without it,
 * the progress view (sector and sign-up dates, as Django's « Avancement »).
 *
 * Every list but the partner groups follows the other filters
 * (`useReportFacets`, the computation the Django pages share): choose a group
 * and only its sectors, MRCs, issues, goals and actions remain.
 *
 * The section follows the shared Administration filter-section pattern (see
 * docs/frontend-styling-guidelines.md → "Filter section"), matching the
 * customizable-assessment tab: a card with a FILTERS label row (Reset + optional
 * Export), a full-width search, a control grid, then active-filter chips under a
 * divider. `onExport`/`exportLabel` place each view's CSV export in the top row.
 */
export function EnterpriseFilters({
  value,
  onChange,
  options,
  showAnswerFilters = false,
  onExport,
  exportLabel,
}: {
  value: EnterpriseFilterState;
  onChange: (next: EnterpriseFilterState) => void;
  options: FilterOptions | undefined;
  showAnswerFilters?: boolean;
  onExport?: () => void;
  exportLabel?: string;
}) {
  const { t } = useTranslation();
  // Debounce the keyword so we don't fire a query per keystroke.
  const [keyword, setKeyword] = useState(value.q ?? "");
  useEffect(() => setKeyword(value.q ?? ""), [value.q]);
  useEffect(() => {
    const id = setTimeout(() => {
      if ((value.q ?? "") !== keyword) onChange({ ...value, q: keyword || undefined });
    }, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  const facets = useReportFacets(showAnswerFilters ? "list" : "progress", value).data;
  const categoryOptions = facets?.sector_categories ?? [];
  const sectorOptions = facets?.sectors ?? [];
  const mrcOptions = facets?.mrc ?? [];
  const issueOptions = facets?.issues ?? [];
  const goalOptions = facets?.goals ?? [];
  const actionOptions = facets?.actions ?? [];
  const answerOptions = facets?.answer_types ?? [];
  const selectedSectors = value.sectors ?? [];

  const toggle = (key: "sectors" | "issues" | "goals" | "actions", id: string) => {
    const current = value[key] ?? [];
    onChange({
      ...value,
      [key]: current.includes(id)
        ? current.filter((v) => v !== id)
        : [...current, id],
    });
  };

  const categoryName = categoryOptions.find(
    (c) => c.id === value.sector_categories,
  )?.name;
  const answerLabel = answerOptions.find((a) => a.id === value.answer_types)?.name;

  const groupName = options?.partner_groups.find(
    (g) => String(g.id) === value.partner_group,
  )?.name;

  // What's set inside the advanced block, so it opens rather than hiding a
  // filter that is silently in force.
  const advancedCount =
    (value.sectors?.length ?? 0) +
    (showAnswerFilters
      ? (value.mrc ? 1 : 0) +
        (value.issues?.length ?? 0) +
        (value.goals?.length ?? 0) +
        (value.actions?.length ?? 0) +
        (value.answer_types ? 1 : 0)
      : (value.start_date ? 1 : 0) + (value.end_date ? 1 : 0));

  const chips: { key: string; label: string; remove: () => void }[] = [];
  if (value.partner_group && groupName) {
    chips.push({
      key: "partner_group",
      label: `${t("reports.filters.partnerGroup")}: ${groupName}`,
      remove: () => onChange({ ...value, partner_group: undefined }),
    });
  }
  if (value.mrc) {
    chips.push({
      key: "mrc",
      label: `${t("reports.filters.mrc")}: ${value.mrc}`,
      remove: () => onChange({ ...value, mrc: undefined }),
    });
  }
  if (value.start_date || value.end_date) {
    chips.push({
      key: "dates",
      label: `${value.start_date ?? "…"} → ${value.end_date ?? "…"}`,
      remove: () => onChange({ ...value, start_date: undefined, end_date: undefined }),
    });
  }
  if (value.sector_categories && categoryName) {
    chips.push({
      key: "category",
      label: `${t("reports.filters.sectorCategory")}: ${categoryName}`,
      remove: () => onChange({ ...value, sector_categories: undefined }),
    });
  }
  const pushChips = (
    key: "sectors" | "issues" | "goals" | "actions",
    opts: { id: string; name: string }[],
  ) => {
    for (const id of value[key] ?? []) {
      const name = opts.find((o) => o.id === id)?.name ?? id;
      chips.push({ key: `${key}-${id}`, label: name, remove: () => toggle(key, id) });
    }
  };
  pushChips("sectors", sectorOptions);
  if (showAnswerFilters) {
    pushChips("issues", issueOptions);
    pushChips("goals", goalOptions);
    pushChips("actions", actionOptions);
    if (value.answer_types && answerLabel) {
      chips.push({
        key: "answer",
        label: `${t("reports.filters.answerType")}: ${answerLabel}`,
        remove: () => onChange({ ...value, answer_types: undefined }),
      });
    }
  }

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        {/* Top row: label + reset + export (export lives here, no dead gap). */}
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {t("reports.filters.heading")}
          </span>
          <span className="flex-1" />
          {chips.length > 0 && (
            <button
              type="button"
              onClick={() => onChange({})}
              className="text-xs font-semibold text-primary hover:underline"
            >
              {t("reports.filters.reset")}
            </button>
          )}
          {onExport && (
            <Button variant="outline" size="sm" onClick={onExport}>
              <Download aria-hidden />
              {exportLabel ?? t("reports.csv.export")}
            </Button>
          )}
        </div>

        <FilterField label={t("reports.filters.keyword")}>
          {(id) => (
            <SearchInput
              id={id}
              value={keyword}
              onChange={setKeyword}
              placeholder={t(
                showAnswerFilters
                  ? "reports.filters.keywordPlaceholder"
                  : "reports.filters.keywordPlaceholderProgress",
              )}
            />
          )}
        </FilterField>

        {/* Everyday controls stay in view; the rest fold into "advanced". */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <FilterField label={t("reports.filters.partnerGroup")}>
            {(id) => (
              <select
                id={id}
                value={value.partner_group ?? ""}
                onChange={(e) =>
                  onChange({ ...value, partner_group: e.target.value || undefined })
                }
                className={cn(filterControlClass, "w-full text-muted-foreground")}
              >
                <option value="">{t("reports.filters.allPartnerGroups")}</option>
                {options?.partner_groups.map((group) => (
                  <option key={group.id} value={String(group.id)}>
                    {group.name}
                  </option>
                ))}
              </select>
            )}
          </FilterField>
          <FilterField label={t("reports.filters.sectorCategory")}>
            {(id) => (
              <select
                id={id}
                value={value.sector_categories ?? ""}
                onChange={(e) =>
                  // A sub-sector picked under another category stays selected and
                  // listed, so it can be cleared — as in the Django pages.
                  onChange({ ...value, sector_categories: e.target.value || undefined })
                }
                className={cn(filterControlClass, "w-full text-muted-foreground")}
              >
                <option value="">{t("reports.filters.allCategories")}</option>
                {categoryOptions.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            )}
          </FilterField>
        </div>

        <AdvancedFilters activeCount={advancedCount}>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            <FilterField label={t("reports.filters.sectors")}>
              {(id) => (
                <MultiSelectFilter
                  id={id}
                  placeholder={t("reports.filters.allSectors")}
                  options={sectorOptions}
                  selected={selectedSectors}
                  onToggle={(sectorId) => toggle("sectors", sectorId)}
                />
              )}
            </FilterField>

            {!showAnswerFilters && (
              <>
                <FilterField label={t("reports.filters.dateFrom")}>
                  {(id) => (
                    <MonthInput
                      id={id}
                      value={value.start_date}
                      onChange={(month) => onChange({ ...value, start_date: month })}
                    />
                  )}
                </FilterField>
                <FilterField label={t("reports.filters.dateTo")}>
                  {(id) => (
                    <MonthInput
                      id={id}
                      value={value.end_date}
                      onChange={(month) => onChange({ ...value, end_date: month })}
                    />
                  )}
                </FilterField>
              </>
            )}

            {showAnswerFilters && (
              <>
                <FilterField label={t("reports.filters.mrc")}>
                  {(id) => (
                    <select
                      id={id}
                      value={value.mrc ?? ""}
                      onChange={(e) => onChange({ ...value, mrc: e.target.value || undefined })}
                      className={cn(filterControlClass, "w-full text-muted-foreground")}
                    >
                      <option value="">{t("reports.filters.allMrcs")}</option>
                      {mrcOptions.map((mrc) => (
                        <option key={mrc.id} value={mrc.id}>
                          {mrc.name}
                        </option>
                      ))}
                    </select>
                  )}
                </FilterField>
                <FilterField label={t("reports.filters.answerType")}>
                  {(id) => (
                    <select
                      id={id}
                      value={value.answer_types ?? ""}
                      onChange={(e) =>
                        onChange({ ...value, answer_types: e.target.value || undefined })
                      }
                      className={cn(filterControlClass, "w-full text-muted-foreground")}
                    >
                      <option value="">{t("reports.filters.allAnswerTypes")}</option>
                      {answerOptions.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.name}
                        </option>
                      ))}
                    </select>
                  )}
                </FilterField>
                <FilterField label={t("reports.filters.issues")}>
                  {(id) => (
                    <MultiSelectFilter
                      id={id}
                      placeholder={t("reports.filters.allIssues")}
                      options={issueOptions}
                      selected={value.issues ?? []}
                      onToggle={(issueId) => toggle("issues", issueId)}
                    />
                  )}
                </FilterField>
                <FilterField label={t("reports.filters.goals")}>
                  {(id) => (
                    <MultiSelectFilter
                      id={id}
                      placeholder={t("reports.filters.allGoals")}
                      options={goalOptions}
                      selected={value.goals ?? []}
                      onToggle={(goalId) => toggle("goals", goalId)}
                    />
                  )}
                </FilterField>
                <FilterField label={t("reports.filters.actions")}>
                  {(id) => (
                    <MultiSelectFilter
                      id={id}
                      placeholder={t("reports.filters.allActions")}
                      options={actionOptions}
                      selected={value.actions ?? []}
                      onToggle={(actionId) => toggle("actions", actionId)}
                    />
                  )}
                </FilterField>
              </>
            )}
          </div>
        </AdvancedFilters>

        {chips.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
            {chips.map((chip) => (
              <FilterChip key={chip.key} label={chip.label} onRemove={chip.remove} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
